class SplitImageAnimatedComponent extends HTMLElement {
  connectedCallback() {
    /* The section renders its two panes with BEM classes only (sections/split-image-animated.liquid).
     * This used to query `[data-frame-left]` / `[data-frame-right]` — attributes that markup has
     * never emitted — so both refs were null and the first `img.src = ...` threw a TypeError inside
     * the preload handler, which is why the section never animated. The data-* hooks are still
     * honoured first in case they are ever added; the class is the guaranteed one. */
    this.imgLeft = this.querySelector('[data-frame-left], .split-image-animated__image--left');
    this.imgRight = this.querySelector('[data-frame-right], .split-image-animated__image--right');
    this.frames = (this.dataset.frames || '')
      .split(',')
      .map((src) => src.trim())
      .filter(Boolean);

    this.duration = parseInt(this.dataset.animationDuration, 10) || 1200;
    this.enableDesktopAnimation = this.dataset.animationDesktop === 'true';
    this.enableMobileAnimation = this.dataset.animationMobile === 'true';

    this.loadedCount = 0;
    this.timer = null;
    this.mediaQuery = window.matchMedia('(min-width: 1024px)');
    this.isDesktop = this.mediaQuery.matches;

    this.onBreakpointChange = () => {
      const nowDesktop = this.mediaQuery.matches;
      if (nowDesktop !== this.isDesktop) {
        this.isDesktop = nowDesktop;
        this.stopAnimation();
        // GOAT: with the preload deferred, a breakpoint flip before the section is near the
        // viewport would otherwise start the animation against frames that have not downloaded and
        // swap in a cold URL. Restart only once the settle counter has completed.
        if (this.loadedCount === this.frames.length) this.startAnimation();
      }
    };
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);

    if (this.frames.length && this.imgLeft) {
      this.schedulePreload();
    }
  }

  /* `image_tag` renders both panes with a `srcset`, and when a srcset has candidates the browser
   * picks from it and IGNORES `src` — so assigning `src` alone updated the attribute while the
   * pixels on screen never changed. Point srcset at the single frame URL (already preloaded, so
   * it is a cache hit) to make the swap the thing that actually renders. */
  setFrame(img, url) {
    if (!img || !url) return;
    img.srcset = url;
    img.src = url;
  }

  /**
   * GOAT (perf): defer the frame downloads until the section is close to the viewport.
   *
   * `preloadFrames()` used to run straight from `connectedCallback`, force-downloading every
   * animation frame at width 1500 — 15 full-bleed photographs across the three split-image
   * sections on the homepage, none of them above the fold — at default priority, competing with
   * the hero's LCP image for bandwidth and connections. The rendered <img> elements are already
   * `loading="lazy"`, so this was the only thing pulling them early.
   *
   * `startAnimation()` is reached solely through `preloadFrames`' settle counter, so gating the
   * preload gates the animation too: it begins the moment the frames land, which is now just
   * before the section scrolls into view rather than during page load.
   */
  schedulePreload() {
    if (typeof IntersectionObserver !== 'function') {
      this.preloadFrames();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.preloadFrames();
      },
      { rootMargin: '600px' }
    );

    observer.observe(this);
  }

  preloadFrames() {
    /* Counts errors as well as loads: a single 404 frame used to leave loadedCount short of the
     * total forever, so the animation never started at all. */
    const onSettled = () => {
      this.loadedCount++;
      if (this.loadedCount === this.frames.length) {
        this.startAnimation();
      }
    };

    this.frames.forEach((src) => {
      const img = new Image();
      img.onload = onSettled;
      img.onerror = onSettled;
      img.src = src;
    });
  }

  startAnimation() {
    this.stopAnimation();
    if (this.isDesktop && this.frames.length >= 2) {
      if (this.enableDesktopAnimation) this.desktopAnimation();
    } else if (this.enableMobileAnimation) {
      this.mobileAnimation();
    }
  }

  // Desktop: two panes visible; alternate which pane advances so the two
  // images never show the same frame at once.
  desktopAnimation() {
    const total = this.frames.length;
    let leftIndex = 0;
    let rightIndex = 1 % total;
    let animateRight = true;

    this.setFrame(this.imgLeft, this.frames[leftIndex]);
    this.setFrame(this.imgRight, this.frames[rightIndex]);

    this.timer = setInterval(() => {
      if (animateRight) {
        rightIndex = (rightIndex + 2) % total;
        if (total % 2 !== 0 && rightIndex === leftIndex) {
          rightIndex = (rightIndex + 1) % total;
        }
        this.setFrame(this.imgRight, this.frames[rightIndex]);
      } else {
        leftIndex = (leftIndex + 2) % total;
        if (total % 2 !== 0 && leftIndex === rightIndex) {
          leftIndex = (leftIndex + 1) % total;
        }
        this.setFrame(this.imgLeft, this.frames[leftIndex]);
      }
      animateRight = !animateRight;
    }, this.duration);
  }

  // Mobile: single pane visible; step through frames sequentially.
  mobileAnimation() {
    const total = this.frames.length;
    let index = 0;

    this.setFrame(this.imgLeft, this.frames[index]);

    this.timer = setInterval(() => {
      index = (index + 1) % total;
      this.setFrame(this.imgLeft, this.frames[index]);
    }, this.duration);
  }

  stopAnimation() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  disconnectedCallback() {
    this.stopAnimation();
    this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
  }
}

if (!customElements.get('split-image-animated-component')) {
  customElements.define('split-image-animated-component', SplitImageAnimatedComponent);
}
